import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Check, Copy, Loader2 } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { ResponsiveModal } from "@/components/responsive-modal";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUserPlanQuery } from "@/features/users/api/user-plan-options";
import { useI18n } from "@/locales/client";
import { useSetVisibility } from "../api/use-set-visibility";
import useShareStudySetModal from "../hooks/use-share-studyset-modal";

const formSchema = z.object({
  visibility: z.enum(["public", "private"] as const),
});

type FormSchema = z.infer<typeof formSchema>;

export const ShareStudysetModal = () => {
  const {
    isOpen,
    onClose,
    publicUrl,
    isPublic,
    studySetId,
    type,
    isAuthenticated,
  } = useShareStudySetModal();

  const t = useI18n();

  const { data, status } = useUserPlanQuery();
  const visibility = isPublic ? "public" : "private";
  const studySetVisbilityMutation = useSetVisibility();

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      visibility: visibility,
    },
  });

  useEffect(() => {
    form.setValue("visibility", visibility);
  }, [visibility]);

  const isVisibilityFieldPublic = form.watch("visibility") === "public";

  const studySetUrl = `${location.origin}${publicUrl}`;
  const [copied, setCopied] = useState(false);

  const onCopy = () => {
    navigator.clipboard.writeText(studySetUrl);
    setCopied(true);

    setTimeout(() => {
      setCopied(false);
    }, 1000);
  };

  function onSubmit(values: FormSchema) {
    if (values.visibility === visibility) {
      onClose();
      return;
    }
    if (!studySetId) {
      return;
    }
    studySetVisbilityMutation.mutate(
      {
        id: studySetId,
        isPublic: values.visibility === "public",
      },
      {
        onSuccess: onClose,
      },
    );
  }

  const renderContent = () => {
    if (!isAuthenticated) {
      return (
        <div className="flex flex-col gap-6 min-w-0">
          <div className="bg-[#E5F1FF] shadow-sm p-4 rounded-md">
            <p className="text-sm font-medium">{t("shareModal.shareLink")}</p>
            <div className="flex items-center gap-x-2 w-full">
              <Link
                href={publicUrl ?? "/"}
                target="_blank"
                className="text-brand-primary truncate font-medium text-sm"
              >
                {studySetUrl}
              </Link>

              <Button
                type="button"
                onClick={onCopy}
                variant="ghost"
                size="icon"
                className="text-muted-foreground shrink-0"
              >
                {copied ? (
                  <Check className="size-4" />
                ) : (
                  <Copy className="size-4" />
                )}
              </Button>
            </div>
          </div>

          <DialogFooter className="w-full sm:justify-between">
            <Button type="button" variant="ghost" onClick={onClose}>
              {t("shareModal.cancel")}
            </Button>
            <Button onClick={onClose} type="button">
              {t("shareModal.done")}
            </Button>
          </DialogFooter>
        </div>
      );
    }
    if (status === "pending") {
      return (
        <div className="flex justify-center items-center h-40">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }
    if (status === "error") {
      return (
        <div className="flex flex-col items-center justify-center h-40 text-center">
          <AlertCircle className="h-8 w-8 text-destructive mb-2" />
          <p className="text-sm text-muted-foreground">
            {t("shareModal.error.description")}
          </p>
        </div>
      );
    }
    return (
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-6 min-w-0"
        >
          {isVisibilityFieldPublic && (
            <div className="bg-[#E5F1FF] shadow-sm p-4 rounded-md">
              <p className="text-sm font-medium">{t("shareModal.shareLink")}</p>
              <div className="flex items-center gap-x-2 w-full">
                <Link
                  href={publicUrl ?? "/"}
                  target="_blank"
                  className="text-brand-primary truncate font-medium text-sm"
                >
                  {studySetUrl}
                </Link>

                <Button
                  type="button"
                  onClick={onCopy}
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground shrink-0"
                >
                  {copied ? (
                    <Check className="size-4" />
                  ) : (
                    <Copy className="size-4" />
                  )}
                </Button>
              </div>
            </div>
          )}
          {type === "personal" && isAuthenticated ? (
            <>
              <FormField
                control={form.control}
                name="visibility"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("shareModal.visibility")}</FormLabel>
                    <Select
                      disabled={studySetVisbilityMutation.isPending}
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t("shareModal.selectVisibility")}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="public">
                          {t("shareModal.public")}
                        </SelectItem>
                        <SelectItem
                          disabled={!data.isSubscribed}
                          value="private"
                        >
                          {data.isSubscribed
                            ? t("shareModal.private")
                            : t("shareModal.upgradeToPrivate")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {isVisibilityFieldPublic
                        ? t("shareModal.publicDescription")
                        : t("shareModal.privateDescription")}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex flex-col-reverse gap-y-4 md:gap-y-0 md:flex-row w-full md:items-center md:justify-between">
                <Button
                  disabled={studySetVisbilityMutation.isPending}
                  type="button"
                  variant="ghost"
                  onClick={onClose}
                >
                  {t("shareModal.cancel")}
                </Button>
                <Button
                  disabled={studySetVisbilityMutation.isPending}
                  type="submit"
                >
                  {studySetVisbilityMutation.isPending ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t("shareModal.updating")}
                    </div>
                  ) : (
                    t("shareModal.done")
                  )}
                </Button>
              </div>
            </>
          ) : (
            <DialogFooter className="w-full sm:justify-between">
              <Button type="button" variant="ghost" onClick={onClose}>
                {t("shareModal.cancel")}
              </Button>
              <Button onClick={onClose} type="button">
                {t("shareModal.done")}
              </Button>
            </DialogFooter>
          )}
        </form>
      </Form>
    );
  };

  return (
    <ResponsiveModal
      title={t("share")}
      description=""
      open={isOpen}
      onOpenChange={onClose}
      form={renderContent()}
    />
  );
};
